/**
 * Build the pagination entry list from a playlist API response (P3-6 / MR-34).
 *
 * Lifted verbatim from the `pageCount > 1` loop in app.vue's `loadData` (which
 * carried a "TODO - Abstract this into a function"). Turns the response's
 * `page_count`/`current_page` into the `{pageNumber, classList}` entries the
 * pagination component renders: the current page gets `classList:'active'`, and
 * a `{pageNumber:'...', classList:'concat-item'}` separator is inserted when the
 * chain is long enough (pageCount > 5) and the current page is far from the end.
 *
 * The branch logic is byte-identical to the original — only the accumulator was
 * renamed (`pageList` → local `list`) and the block wrapped in a function.
 * `loadData` spreads the result into its cross-station `pageList`.
 *
 * @param {{page_count:number, current_page:number}} data raw API response
 * @returns {Array<{pageNumber:(number|string), classList:string}>}
 */
export const buildPageList = (data) => {
  const list = [];
  const pageCount = data.page_count;

  if (pageCount > 1) {
    for (let i = 1, l = pageCount; i <= l; i++) {
      const currentPage = data.current_page;
      const elementLimit = 5;

      // Create the content for the pagination component
      if (pageCount <= elementLimit) {
        // Too few items to ever add a separator. Just add them all
        list.push({
          pageNumber: i,
          classList: i === currentPage ? 'active' : '',
        });
      } else {
        // Need to figure out where we are in the chain to get the formatting right
        if (currentPage > pageCount - (elementLimit - 1)) {
          // We're almost at the end. No seperator
          if (i >= pageCount - (elementLimit - 1)) {
            list.push({
              pageNumber: i,
              classList: i === currentPage ? 'active' : '',
            });
          }
        } else if (i === currentPage - 1 || i >= pageCount - 1) {
          // Before the current page and after the concat item
          list.push({
            pageNumber: i,
            classList: '',
          });
        } else if (i === currentPage) {
          // Current page
          list.push({
            pageNumber: i,
            classList: 'active',
          });
        } else if (currentPage === 1 && i === currentPage + 2) {
          // We only print the current page + 2 if we're on page 1
          list.push(
            {
              pageNumber: i,
              classList: '',
            },
            {
              pageNumber: '...',
              classList: 'concat-item',
            }
          );
        } else if (i === currentPage + 1) {
          // We print the seperator after the current page when it's not the current page
          if (currentPage === 1) {
            list.push({
              pageNumber: i,
              classList: '',
            });
          } else {
            list.push(
              {
                pageNumber: i,
                classList: '',
              },
              {
                pageNumber: '...',
                classList: 'concat-item',
              }
            );
          }
        }
      }
    }
  }

  return list;
};
